import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Search,
  X,
  // Food
  Salad,
  UtensilsCrossed,
  Utensils,
  CookingPot,
  Soup,
  Beef,
  Drumstick,
  Fish,
  HandPlatter,
  Pizza,
  Sandwich,
  EggFried,
  Croissant,
  Cookie,
  Cake,
  CakeSlice,
  IceCream,
  IceCream2,
  Donut,
  // Wine / drinks
  Wine,
  WineOff,
  Grape,
  GlassWater,
  Martini,
  Beer,
  BeerOff,
  CupSoda,
  Milk,
  Coffee,
  FlaskConical,
  FlaskRound,
  // Misc
  ChefHat,
  Carrot,
  Apple,
  Cherry,
  Banana,
  Citrus,
  Leaf,
  Wheat,
  WheatOff,
  Flame,
  Snowflake,
  Sparkles,
  Star,
  Heart,
  Crown,
} from "lucide-react";

export const ICON_OPTIONS = {
  Salad,
  UtensilsCrossed,
  Utensils,
  CookingPot,
  Soup,
  Beef,
  Drumstick,
  Fish,
  HandPlatter,
  Pizza,
  Sandwich,
  EggFried,
  Croissant,
  Cookie,
  Cake,
  CakeSlice,
  IceCream,
  IceCream2,
  Donut,
  Wine,
  WineOff,
  Grape,
  GlassWater,
  Martini,
  Beer,
  BeerOff,
  CupSoda,
  Milk,
  Coffee,
  FlaskConical,
  FlaskRound,
  ChefHat,
  Carrot,
  Apple,
  Cherry,
  Banana,
  Citrus,
  Leaf,
  Wheat,
  WheatOff,
  Flame,
  Snowflake,
  Sparkles,
  Star,
  Heart,
  Crown,
};

export const ICON_NAMES = Object.keys(ICON_OPTIONS);

export function resolveIcon(name) {
  return ICON_OPTIONS[name] ?? UtensilsCrossed;
}

export function IconPicker({ value, onChange, testid = "icon-picker" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const Current = resolveIcon(value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ICON_NAMES;
    return ICON_NAMES.filter((n) => n.toLowerCase().includes(q));
  }, [query]);

  return (
    <div>
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 rounded-xl border border-border/80 bg-background/40 px-3 py-3 text-left transition-colors hover:border-gold/50 focus:border-gold/60"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-gold/20 to-gold-muted/10 text-gold ring-1 ring-gold/30">
          <Current className="h-4 w-4" strokeWidth={1.6} />
        </span>
        <span className="flex-1 truncate text-sm text-foreground">
          {value || "UtensilsCrossed"}
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 26 }}
          className="text-muted-foreground"
        >
          <ChevronDown className="h-4 w-4" />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`${testid}-panel`}
            className="overflow-hidden"
          >
            <div className="mt-2 overflow-hidden rounded-xl border border-gold/20 bg-background/40">
              <div className="flex items-center gap-2 border-b border-border/40 px-3 py-2">
                <Search className="h-3.5 w-3.5 text-gold-muted" />
                <input
                  data-testid={`${testid}-search`}
                  type="text"
                  autoFocus
                  placeholder="Αναζήτηση εικονιδίου…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1 bg-transparent py-1.5 text-sm text-foreground outline-none placeholder:text-muted-foreground/60"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="text-muted-foreground hover:text-gold"
                    aria-label="Καθαρισμός"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="max-h-56 overflow-y-auto p-2">
                {filtered.length === 0 ? (
                  <p className="px-2 py-6 text-center text-xs italic text-muted-foreground">
                    Δεν βρέθηκε εικονίδιο.
                  </p>
                ) : (
                  <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6">
                    {filtered.map((name) => {
                      const Icon = ICON_OPTIONS[name];
                      const isActive = name === value;
                      return (
                        <button
                          key={name}
                          type="button"
                          data-testid={`${testid}-${name}`}
                          onClick={() => {
                            onChange(name);
                            setOpen(false);
                          }}
                          title={name}
                          className={
                            "group flex flex-col items-center gap-1 rounded-lg px-2 py-2 transition-all " +
                            (isActive
                              ? "bg-gold/20 text-gold ring-1 ring-gold/50"
                              : "text-foreground hover:bg-card/80 hover:text-gold")
                          }
                        >
                          <Icon className="h-5 w-5" strokeWidth={1.6} />
                          <span className="w-full truncate text-[0.55rem] uppercase tracking-wider text-muted-foreground group-hover:text-gold-muted">
                            {name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
